import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Group,
  Paper,
  Progress,
  RingProgress,
  Stack,
  Tabs,
  Text,
  Timeline,
  useMantineTheme,
} from "@mantine/core";
import _ from "lodash";
import moment from "moment";
import { useServerApi } from "../hooks/userServerApi";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { computeLevel } from "../helpers/levelProgress";
import {
  buildPromotionRankTimeline,
  buildTrackPathway,
  collectPointTypeTabs,
  conditionGap,
  conditionGapCopy,
  idOf,
  promotionProgressHeadline,
  resolvePointTypeLabel,
} from "../helpers/promotionProgress";
import { UserPointHistory } from "./pointBalances";
import { POINT_ADJUSTMENT_PATH } from "../helpers/pointTransactionDisplay";

const formatWhen = (value) =>
  value ? moment(value).format("YYYY-MM-DD HH:mm") : null;

const formatCount = (value) =>
  Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });

const formatUsdWhole = (value) =>
  `USD ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const usePromotionTrackData = (form, id) => {
  const [api] = useServerApi();
  const [catalog, setCatalog] = useState([]);
  const [levelSetting, setLevelSetting] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const userId = _.get(form?.values, "_id") ?? id;
  const consultant = form?.values?.consultantInfo || {};
  const rankCode = consultant.promotionRankCode || null;
  const trackRef = consultant.promotionTrack?._id || consultant.promotionTrack;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [settingResult, pointTypeResult, progressResult] =
          await Promise.all([
            api.search({ apiEntity: "setting", pageSize: 1 }),
            api.search({
              apiEntity: "pointType",
              pageSize: 100,
              preQuery: { status: "ACTIVE" },
            }),
            userId && trackRef
              ? api.getPromotionProgress(userId)
              : Promise.resolve(null),
          ]);
        if (cancelled) return;
        setLevelSetting(settingResult?.docs?.[0]?.level || null);
        setCatalog(pointTypeResult?.docs || []);
        setProgress(progressResult || null);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load promotion track");
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, rankCode, String(trackRef || "")]);

  return {
    catalog,
    levelSetting,
    progress,
    error,
    userId,
    consultant,
    trackRef,
  };
};

const PATHWAY_STYLE = {
  earned: { color: "cyan", filled: true },
  current: { color: "green", filled: true },
  ready: { color: "teal", filled: false },
  ahead: { color: "gray", filled: false },
};

const PATHWAY_DOT_RAIL = 44;

const PathwayStep = ({ step }) => {
  const theme = useMantineTheme();
  const style = PATHWAY_STYLE[step.state] || PATHWAY_STYLE.ahead;
  const palette = theme.colors[style.color] || theme.colors.gray;
  const isCurrent = step.state === "current";
  const size = isCurrent ? 44 : 34;
  const background = style.filled ? palette[6] : "transparent";
  const border = style.filled ? palette[6] : palette[5];
  const textColor = style.filled
    ? theme.white
    : theme.colorScheme === "dark"
      ? palette[3]
      : palette[7];

  return (
    <Stack spacing={6} align="center" sx={{ flex: "0 0 auto" }}>
      <Box
        sx={{
          position: "relative",
          width: size,
          height: PATHWAY_DOT_RAIL,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "@keyframes promotionCurrentRipple": {
            "0%": { transform: "scale(1)", opacity: 0.55 },
            "100%": { transform: "scale(2.2)", opacity: 0 },
          },
        }}
      >
        {isCurrent &&
          [0, 0.7].map((delay) => (
            <Box
              key={delay}
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 999,
                border: `2px solid ${palette[5]}`,
                animation: "promotionCurrentRipple 1.8s ease-out infinite",
                animationDelay: `${delay}s`,
                pointerEvents: "none",
              }}
            />
          ))}
        <Box
          sx={{
            position: "relative",
            width: size,
            height: size,
            borderRadius: 999,
            border: `2px solid ${border}`,
            background,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <Text
            size={isCurrent ? "sm" : "xs"}
            weight={800}
            sx={{ color: textColor, lineHeight: 1 }}
          >
            {step.code}
          </Text>
        </Box>
      </Box>
      {step.name && (
        <Text
          size="xs"
          align="center"
          weight={isCurrent ? 700 : 500}
          color={step.state === "ahead" ? "dimmed" : undefined}
          sx={{ lineHeight: 1.2, whiteSpace: "nowrap" }}
        >
          {step.name}
        </Text>
      )}
      {step.cname && (
        <Text
          size="xs"
          align="center"
          color="dimmed"
          sx={{ lineHeight: 1.2, whiteSpace: "nowrap", marginTop: -2 }}
        >
          {step.cname}
        </Text>
      )}
      {isCurrent && (
        <Badge size="xs" color="green" variant="light">
          Current
        </Badge>
      )}
      {step.state === "ready" && (
        <Badge size="xs" color="teal" variant="light">
          Ready
        </Badge>
      )}
    </Stack>
  );
};

const PathwayConnector = ({ fromState }) => {
  const theme = useMantineTheme();
  const filled = fromState === "earned" || fromState === "current";
  const ready = fromState === "ready";
  const color = filled
    ? theme.colors.cyan[5]
    : ready
      ? theme.colors.teal[5]
      : theme.colorScheme === "dark"
        ? theme.colors.dark[4]
        : theme.colors.gray[4];

  return (
    <Box
      sx={{
        flex: "1 1 48px",
        minWidth: 48,
        height: 2,
        marginTop: PATHWAY_DOT_RAIL / 2 - 1,
        background: color,
      }}
    />
  );
};

const TrackPathway = ({ ranks, currentRankCode, qualifiesTo }) => {
  const pathway = buildTrackPathway({
    ranks,
    currentRankCode,
    qualifiesTo,
  });
  if (!pathway.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        minWidth: 0,
        overflowX: "auto",
        overflowY: "visible",
        paddingTop: 12,
        paddingBottom: 8,
        paddingLeft: 4,
        paddingRight: 4,
      }}
    >
      {pathway.map((step, index) => (
        <React.Fragment key={step.code}>
          <PathwayStep step={step} />
          {index < pathway.length - 1 && (
            <PathwayConnector fromState={step.state} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

const conditionTitle = (item, catalog = []) => {
  if (item?.type === "POINT_BALANCE") {
    return (
      item.pointTypeName ||
      item.pointTypeCode ||
      resolvePointTypeLabel(item.pointType, catalog)
    );
  }
  return `Direct ${item?.minRankCode || ""}+`;
};

const formatHaveNeed = (item, gap) => {
  if (item?.type === "POINT_BALANCE") {
    return {
      have: formatUsdWhole(gap.have),
      need: formatUsdWhole(gap.need),
    };
  }
  return {
    have: formatCount(gap.have),
    need: formatCount(gap.need),
  };
};

const ConditionGapCard = ({ item, catalog = [] }) => {
  const gap = conditionGap(item);
  const amounts = formatHaveNeed(item, gap);
  const color = gap.passed ? "teal" : "orange";

  return (
    <Paper withBorder radius="md" p="md">
      <Group position="apart" align="flex-start" mb={8} noWrap>
        <Text size="sm" weight={700}>
          {conditionTitle(item, catalog)}
        </Text>
        <Badge color={color} variant="light">
          {gap.passed ? "Met" : "Missing"}
        </Badge>
      </Group>
      <Text size="lg" weight={800} sx={{ flex: 1 }}>
        {conditionGapCopy(item, catalog)}
      </Text>
      <Progress
        value={gap.percent}
        color={color}
        size="md"
        radius="xl"
        mt="md"
        mb="sm"
      />
      <Group position="apart">
        <Text size="xs" color="dimmed">
          Have {amounts.have}
        </Text>
        <Text size="xs" color="dimmed">
          Need {amounts.need}
        </Text>
      </Group>
    </Paper>
  );
};

const LevelRing = ({ levelProgress }) => {
  const theme = useMantineTheme();
  const percent =
    levelProgress?.pointsPerLevel > 0
      ? Math.min(
          100,
          Math.round(
            (Number(levelProgress.remainder) / levelProgress.pointsPerLevel) *
              100
          )
        )
      : 0;

  return (
    <Stack spacing={4} align="center" sx={{ flex: "0 0 auto" }}>
      <RingProgress
        size={168}
        thickness={14}
        roundCaps
        sections={[{ value: percent, color: "cyan" }]}
        label={
          <Stack spacing={0} align="center">
            <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
              Level
            </Text>
            <Text
              weight={800}
              sx={{
                fontSize: 32,
                lineHeight: 1,
                color:
                  theme.colorScheme === "dark"
                    ? theme.white
                    : theme.colors.dark[7],
              }}
            >
              {levelProgress?.level ?? 0}
            </Text>
          </Stack>
        }
      />
      {levelProgress?.nextLevelAt != null && (
        <Text size="xs" color="dimmed" align="center">
          {formatUsdWhole(levelProgress.remainder)} /{" "}
          {formatUsdWhole(levelProgress.pointsPerLevel)}
        </Text>
      )}
    </Stack>
  );
};

const AchievedStat = ({ label, value }) => (
  <Paper withBorder radius="md" p="sm">
    <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
      {label}
    </Text>
    <Text weight={800} mt={4}>
      {value}
    </Text>
  </Paper>
);

const PathToNext = ({
  evaluation,
  headline,
  trackName,
  catalog = [],
  balances = [],
  levelProgress,
}) => {
  const items = evaluation?.progress?.items || [];
  const logic = evaluation?.progress?.logic;
  const pointTabs = collectPointTypeTabs({ balances, catalog });

  if (!evaluation) {
    return (
      <Text size="sm" color="dimmed">
        Loading remaining conditions…
      </Text>
    );
  }

  const leftColumn = (
    <Box
      sx={{
        flex: "0 0 220px",
        width: 220,
        display: "flex",
        justifyContent: "center",
        paddingTop: 8,
      }}
    >
      <LevelRing levelProgress={levelProgress} />
    </Box>
  );

  if (headline.isTop) {
    return (
      <Group align="flex-start" spacing="xl" noWrap>
        {leftColumn}
        <Stack spacing="sm" sx={{ flex: 1, minWidth: 0 }}>
          {pointTabs.map((tab) => {
            const row = balances.find(
              (entry) => idOf(entry.pointType) === tab.id
            );
            return (
              <AchievedStat
                key={tab.id}
                label={tab.label}
                value={formatUsdWhole(row?.amount || 0)}
              />
            );
          })}
          <Text size="sm" color="dimmed">
            At the top of {trackName || "the track"} — no further rank
            conditions.
          </Text>
        </Stack>
      </Group>
    );
  }

  if (!items.length) {
    return (
      <Group align="flex-start" spacing="xl" noWrap>
        {leftColumn}
        <Text size="sm" color="dimmed">
          No promotion conditions on the current rank.
        </Text>
      </Group>
    );
  }

  return (
    <Group align="flex-start" spacing="xl" noWrap>
      {leftColumn}
      <Stack spacing="sm" sx={{ flex: 1, minWidth: 0 }}>
        <Group position="right">
          {logic === "OR" ? (
            <Text size="xs" color="dimmed">
              Any one of these is enough
            </Text>
          ) : (
            <Text size="xs" color="dimmed">
              All of these are required
            </Text>
          )}
        </Group>
        {items.map((item, index) => (
          <ConditionGapCard
            key={`${item.type}-${item.minRankCode || item.pointTypeCode || index}`}
            item={item}
            catalog={catalog}
          />
        ))}
      </Stack>
    </Group>
  );
};

export const UserPromotionSummary = ({ form, id }) => {
  const theme = useMantineTheme();
  const { progress, error, consultant, trackRef } = usePromotionTrackData(
    form,
    id
  );
  const track = progress?.track || consultant.promotionTrack || null;
  const rankCode = consultant.promotionRankCode || null;
  const ranks = track?.ranks || [];
  const evaluation = progress?.evaluation;
  const headline = promotionProgressHeadline(evaluation);
  const currentRank =
    ranks.find((rank) => rank.code === rankCode) ||
    evaluation?.currentRank ||
    null;
  const trackName = track?.name || "Promotion track";
  const rankCodeLabel = currentRank?.code || rankCode || "—";
  const rankName = currentRank?.name || "";
  const rankCname = currentRank?.cname || "";
  const heroAccent = headline.qualifiesTo
    ? "teal"
    : headline.isTop
      ? "grape"
      : "cyan";

  if (error) {
    return (
      <Alert color="red" mb="md">
        {error}
      </Alert>
    );
  }

  if (!trackRef && !track) {
    return (
      <Text size="sm" color="dimmed">
        Assign a promotion track on the Overview tab to see rank progress.
      </Text>
    );
  }

  return (
    <Paper withBorder radius="md" p="md" sx={{ overflow: "visible" }}>
      <Group position="apart" align="flex-start" mb="md" noWrap>
        <Group spacing="sm" align="center" noWrap>
          <Text
            weight={800}
            sx={{
              fontSize: 28,
              lineHeight: 1.1,
              color:
                theme.colorScheme === "dark"
                  ? theme.white
                  : theme.colors.dark[7],
            }}
          >
            {rankCodeLabel}
          </Text>
          {(rankName || rankCname) && (
            <Box>
              {rankName && (
                <Text size="sm" weight={600}>
                  {rankName}
                </Text>
              )}
              {rankCname && (
                <Text size="sm" color="dimmed">
                  {rankCname}
                </Text>
              )}
            </Box>
          )}
          {headline.isTop && <Badge color="grape">Top rank</Badge>}
          {headline.qualifiesTo && (
            <Badge color="green">
              Ready to promote to {headline.qualifiesTo}
            </Badge>
          )}
        </Group>
        <Text
          weight={800}
          align="right"
          sx={{
            fontSize: 16,
            lineHeight: 1.3,
            color: theme.colors[heroAccent]?.[4] || theme.colors.cyan[4],
          }}
        >
          {trackName}
        </Text>
      </Group>
      <TrackPathway
        ranks={ranks}
        currentRankCode={rankCode || headline.currentCode}
        qualifiesTo={headline.qualifiesTo}
      />
    </Paper>
  );
};

export const UserPromotionPathToNext = ({ form, id }) => {
  const { catalog, levelSetting, progress, error, consultant, trackRef } =
    usePromotionTrackData(form, id);
  const balances = form?.values?.pointBalances || [];
  const track = progress?.track || consultant.promotionTrack || null;
  const evaluation = progress?.evaluation;
  const headline = promotionProgressHeadline(evaluation);
  const trackName = track?.name || "";
  const levelPointTypeId = idOf(levelSetting?.pointType);
  const levelBalance = balances.find(
    (row) => idOf(row.pointType) === levelPointTypeId
  );
  const levelProgress = computeLevel({
    amount: Number(levelBalance?.amount) || 0,
    pointsPerLevel: levelSetting?.pointsPerLevel,
  });

  if (error) {
    return (
      <Alert color="red" mb="md">
        {error}
      </Alert>
    );
  }

  if (!trackRef && !track) {
    return (
      <Text size="sm" color="dimmed">
        Assign a promotion track on the Overview tab to see rank progress.
      </Text>
    );
  }

  return (
    <PathToNext
      evaluation={evaluation}
      headline={headline}
      trackName={trackName}
      catalog={catalog}
      balances={balances}
      levelProgress={levelProgress}
    />
  );
};

export const UserPromotionHistory = ({ form, id }) => {
  const { progress, consultant } = usePromotionTrackData(form, id);
  const track = progress?.track || consultant.promotionTrack || null;
  const ranks = track?.ranks || [];
  const timeline = buildPromotionRankTimeline({
    history: consultant.promotionHistory || [],
    currentRankCode: consultant.promotionRankCode || null,
    ranks,
  });

  if (!timeline.length) {
    return (
      <Text size="sm" color="dimmed">
        No promotion history yet.
      </Text>
    );
  }

  return (
    <Timeline active={0} bulletSize={16} lineWidth={2}>
      {timeline.map((row) => (
        <Timeline.Item
          key={row.rankCode}
          color={row.isCurrent ? "cyan" : "gray"}
          title={
            <Group spacing={8}>
              <Text weight={700} size="sm">
                {row.rankCode}
                {row.rankName ? ` ${row.rankName}` : ""}
              </Text>
              {row.isCurrent && (
                <Badge size="xs" variant="light">
                  Current
                </Badge>
              )}
            </Group>
          }
        >
          <Text size="xs" color="dimmed">
            {formatWhen(row.at) || "—"}
            {row.source ? ` · ${row.source}` : ""}
          </Text>
        </Timeline.Item>
      ))}
    </Timeline>
  );
};

export const UserPointTypeTransactions = ({ form, id }) => {
  const { catalog, userId } = usePromotionTrackData(form, id);
  const tabNav = useTabNavigate();
  const balances = form?.values?.pointBalances || [];
  const pointTabs = useMemo(
    () => collectPointTypeTabs({ balances, catalog }),
    [balances, catalog]
  );
  const [activeTxnTab, setActiveTxnTab] = useState(null);

  useEffect(() => {
    if (!pointTabs.length) {
      setActiveTxnTab(null);
      return;
    }
    if (!activeTxnTab || !pointTabs.some((tab) => tab.id === activeTxnTab)) {
      setActiveTxnTab(pointTabs[0].id);
    }
  }, [pointTabs, activeTxnTab]);

  if (!pointTabs.length) {
    return (
      <Text size="sm" color="dimmed">
        No point types configured.
      </Text>
    );
  }

  return (
    <Stack spacing="sm">
      <Group position="apart" align="center">
        <Text size="xs" color="dimmed">
          Accruals come from orders after cooling-off. This list is the points
          log — posted rows cannot be edited. Add a manual ADJUSTMENT in Point
          Transactions.
        </Text>
        <Anchor
          component="button"
          type="button"
          size="sm"
          weight={600}
          onClick={() =>
            tabNav.open({
              pathname: POINT_ADJUSTMENT_PATH,
              label: "Point Transactions",
            })
          }
        >
          Add adjustment
        </Anchor>
      </Group>
      <Tabs value={activeTxnTab} onTabChange={setActiveTxnTab} variant="outline">
        <Tabs.List mb="sm">
          {pointTabs.map((tab) => (
            <Tabs.Tab key={tab.id} value={tab.id}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {pointTabs.map((tab) => (
          <Tabs.Panel key={tab.id} value={tab.id}>
            {activeTxnTab === tab.id && (
              <UserPointHistory form={form} id={userId} pointTypeId={tab.id} />
            )}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
};
